import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import {
  candidateErrorMessage,
  deleteCandidate,
} from "@shared/api/adminApi.ts";
import { Alert, Button } from "@shared/components/Button.tsx";
import { Icon } from "@shared/components/Icon.tsx";
import { Modal } from "@shared/components/Modal.tsx";
import { Loading, Skeleton } from "@shared/components/Skeleton.tsx";
import { useToast } from "@shared/components/Toast.tsx";
import { describeDetails, detailRows } from "@shared/lib/details.ts";
import {
  DISTRICTS_SORTED,
  districtLabel,
  genderLabel,
  languageLabel,
  REGIONS,
  regionLabel,
  stateLabel,
  typeLabel,
  typeShort,
} from "@shared/lib/enums.ts";
import {
  ageFrom,
  formatDateMr,
  formatDateTimeAdmin,
  formatNumberAdmin,
} from "@shared/lib/format.ts";
import { useAsync } from "@shared/lib/useAsync.ts";
import type { Candidate } from "@shared/types.ts";
import { AdminBanner } from "../Layout.tsx";
import {
  fetchCandidates,
  fetchDetails,
  invalidateCandidates,
  invalidateDetails,
  useRowDetails,
} from "../data.ts";

const PAGE_SIZES = [25, 50, 100];

export function CandidatesPage() {
  const [params, setParams] = useSearchParams();
  const list = useAsync(() => fetchCandidates(true), []);
  const toast = useToast();
  const navigate = useNavigate();

  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [region, setRegion] = useState("");
  const [district, setDistrict] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [toDelete, setToDelete] = useState<Candidate | null>(null);

  const viewId = params.get("view");
  const viewing = list.data?.find((c) => c.id === viewId) ?? null;

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (list.data ?? [])
      .filter(
        (c) =>
          (!type ||
            (type === "open-group"
              ? c.candidate_type !== "school"
              : c.candidate_type === type)) &&
          (!region || c.region === region) &&
          (!district || c.district === district) &&
          (!needle ||
            c.name.toLowerCase().includes(needle) ||
            c.phone.includes(needle) ||
            c.id.toLowerCase().startsWith(needle) ||
            (c.email ?? "").toLowerCase().includes(needle) ||
            c.city.toLowerCase().includes(needle)),
      )
      .sort((a, b) => b.created_at.localeCompare(a.created_at));
  }, [list.data, q, type, region, district]);

  useEffect(() => setPage(1), [q, type, region, district, pageSize]);

  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, pages);
  const rows = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);
  const details = useRowDetails(rows);
  const anyFilter = q || type || region || district;

  const onDeleted = (c: Candidate) => {
    invalidateCandidates();
    invalidateDetails(c.id);
    setToDelete(null);
    if (viewId === c.id) setParams({});
    toast("success", `${c.name} यांची नोंदणी हटवली.`);
    list.reload();
  };

  return (
    <>
      <AdminBanner
        title="स्पर्धक"
        sub={
          list.data
            ? `एकूण ${formatNumberAdmin(list.data.length)} नोंदणीकृत स्पर्धक`
            : "यादी लोड होत आहे…"
        }
        actions={
          <Link to="/candidates/new" className="btn btn-primary btn-md">
            <Icon name="plus" size={18} />
            <span>नवीन स्पर्धक</span>
          </Link>
        }
      />
      <div className="admin-container admin-content">
        <div className="filters">
          <label className="search">
            <Icon name="search" size={18} />
            <input
              id="cand_search"
              type="search"
              placeholder="नाव, मोबाईल, ईमेल, आयडी, शहर…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label="शोधा"
            />
          </label>
          <select
            id="cand_type"
            className="input select"
            value={type}
            onChange={(e) => setType(e.target.value)}
            aria-label="गट"
          >
            <option value="">सर्व गट</option>
            <option value="school">शालेय गट</option>
            <option value="open-group">खुला गट (सर्व)</option>
            <option value="college">
              &nbsp;&nbsp;खुला · महाविद्यालयीन विद्यार्थी
            </option>
            <option value="open">&nbsp;&nbsp;खुला · नोकरी / व्यवसाय</option>
          </select>
          <select
            id="cand_region"
            className="input select"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            aria-label="विभाग"
          >
            <option value="">सर्व विभाग</option>
            {REGIONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
          <select
            id="cand_district"
            className="input select"
            value={district}
            onChange={(e) => setDistrict(e.target.value)}
            aria-label="जिल्हा"
          >
            <option value="">सर्व जिल्हे</option>
            {DISTRICTS_SORTED.map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </select>
          {anyFilter && (
            <Button
              variant="ghost"
              size="sm"
              icon="close"
              onClick={() => {
                setQ("");
                setType("");
                setRegion("");
                setDistrict("");
              }}
            >
              फिल्टर काढा
            </Button>
          )}
        </div>

        {list.status === "error" && !list.data && (
          <Alert
            tone="error"
            title="स्पर्धकांची यादी लोड होऊ शकली नाही"
            action={
              <Button
                size="sm"
                variant="secondary"
                icon="refresh"
                onClick={list.reload}
              >
                पुन्हा प्रयत्न करा
              </Button>
            }
          />
        )}

        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>स्पर्धक</th>
                <th>गट</th>
                <th>शाळा / महाविद्यालय / व्यवसाय</th>
                <th>संपर्क</th>
                <th>जिल्हा · विभाग</th>
                <th>नोंदणी</th>
                <th>
                  <span className="sr-only">कृती</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {!list.data && list.status === "loading" && <TableSkeleton />}
              {list.data && rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="table-empty">
                    {anyFilter
                      ? "या फिल्टरनुसार एकही स्पर्धक सापडला नाही."
                      : "अद्याप एकही नोंदणी झालेली नाही."}
                  </td>
                </tr>
              )}
              {rows.map((c) => {
                const d = details[c.id];
                return (
                  <tr
                    key={c.id}
                    onClick={() => setParams({ view: c.id })}
                    className="is-clickable"
                  >
                    <td data-label="स्पर्धक">
                      <strong className="cell-name">{c.name}</strong>
                      <span className="cell-sub">
                        {genderLabel(c.gender)} · {ageFrom(c.dob) ?? "—"} वर्षे
                      </span>
                    </td>
                    <td data-label="गट">
                      <span className={`chip chip-${c.candidate_type}`}>
                        {typeShort(c.candidate_type)}
                      </span>
                    </td>
                    <td
                      data-label="शाळा / महाविद्यालय / व्यवसाय"
                      className="cell-details"
                    >
                      {d === undefined ? (
                        <Skeleton w="85%" h={14} />
                      ) : d === "error" ? (
                        <span className="muted">लोड झाले नाही</span>
                      ) : (
                        describeDetails(c.candidate_type, d)
                      )}
                    </td>
                    <td data-label="संपर्क" className="num">
                      {c.phone}
                      {c.email && <span className="cell-sub">{c.email}</span>}
                    </td>
                    <td data-label="जिल्हा · विभाग">
                      {districtLabel(c.district)}
                      <span className="cell-sub">{regionLabel(c.region)}</span>
                    </td>
                    <td data-label="नोंदणी" className="num nowrap">
                      {formatDateTimeAdmin(c.created_at)}
                    </td>
                    <td
                      className="cell-actions"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        type="button"
                        className="icon-btn"
                        title="पाहा"
                        aria-label={`${c.name} — पाहा`}
                        onClick={() => setParams({ view: c.id })}
                      >
                        <Icon name="eye" size={18} />
                      </button>
                      <button
                        type="button"
                        className="icon-btn"
                        title="संपादित करा"
                        aria-label={`${c.name} — संपादित करा`}
                        onClick={() => navigate(`/candidates/${c.id}/edit`)}
                      >
                        <Icon name="edit" size={18} />
                      </button>
                      <button
                        type="button"
                        className="icon-btn icon-btn--danger"
                        title="हटवा"
                        aria-label={`${c.name} — हटवा`}
                        onClick={() => setToDelete(c)}
                      >
                        <Icon name="trash" size={18} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {list.data && filtered.length > 0 && (
          <div className="pager">
            <span className="muted num">
              {formatNumberAdmin((safePage - 1) * pageSize + 1)}–
              {formatNumberAdmin(
                Math.min(safePage * pageSize, filtered.length),
              )}{" "}
              / {formatNumberAdmin(filtered.length)}
            </span>
            <div className="pager__controls">
              <label className="muted pager__size">
                प्रति पान
                <select
                  className="input select"
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                >
                  {PAGE_SIZES.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>
              <Button
                size="sm"
                variant="secondary"
                icon="chevronLeft"
                disabled={safePage <= 1}
                onClick={() => setPage(safePage - 1)}
              >
                मागील
              </Button>
              <span className="num pager__page">
                पान {safePage} / {pages}
              </span>
              <Button
                size="sm"
                variant="secondary"
                iconRight="chevronRight"
                disabled={safePage >= pages}
                onClick={() => setPage(safePage + 1)}
              >
                पुढील
              </Button>
            </div>
          </div>
        )}
      </div>

      <CandidateDrawer
        candidate={viewing}
        loadingList={!!viewId && !list.data}
        onClose={() => setParams({})}
        onEdit={(c) => navigate(`/candidates/${c.id}/edit`)}
        onDelete={(c) => setToDelete(c)}
      />
      <DeleteDialog
        candidate={toDelete}
        onClose={() => setToDelete(null)}
        onDeleted={onDeleted}
      />
    </>
  );
}

function TableSkeleton() {
  return (
    <>
      {Array.from({ length: 8 }, (_, i) => (
        <tr key={i} aria-hidden="true">
          <td>
            <Skeleton w="70%" h={16} />
            <Skeleton w="40%" h={12} style={{ marginTop: 6 }} />
          </td>
          <td>
            <Skeleton w={70} h={22} r={999} />
          </td>
          <td>
            <Skeleton w="90%" h={14} />
          </td>
          <td>
            <Skeleton w="80%" h={14} />
          </td>
          <td>
            <Skeleton w="70%" h={14} />
          </td>
          <td>
            <Skeleton w="80%" h={14} />
          </td>
          <td>
            <Skeleton w={90} h={28} />
          </td>
        </tr>
      ))}
    </>
  );
}

/* ------------------------------ view modal ------------------------------- */

function CandidateDrawer({
  candidate: c,
  loadingList,
  onClose,
  onEdit,
  onDelete,
}: {
  candidate: Candidate | null;
  loadingList: boolean;
  onClose: () => void;
  onEdit: (c: Candidate) => void;
  onDelete: (c: Candidate) => void;
}) {
  const details = useAsync(
    () => (c ? fetchDetails(c) : Promise.resolve(null)),
    [c?.id],
  );
  const open = !!c || loadingList;

  const base: [string, string][] = c
    ? [
        ["नोंदणी आयडी", c.id],
        [
          "जन्मतारीख",
          `${formatDateMr(c.dob, true)} (${ageFrom(c.dob) ?? "—"} वर्षे)`,
        ],
        ["लिंग", genderLabel(c.gender)],
        ["स्पर्धेसाठी निवडलेली भाषा", languageLabel(c.language)],
        ["मोबाईल", c.phone],
        ["व्हॉट्सअ‍ॅप", c.whatsapp_phone ?? "—"],
        ["ईमेल", c.email ?? "—"],
        ["पत्ता", c.address],
        ["गाव / शहर", c.city],
        ["जिल्हा", districtLabel(c.district)],
        ["विभाग", regionLabel(c.region)],
        ["राज्य", stateLabel(c.state)],
        ["पिनकोड", c.pincode],
        ["माहिती, नियम आणि अटी मान्य", c.accepted_toc_rules ? "होय" : "नाही"],
        ["नोंदणी दिनांक", formatDateTimeAdmin(c.created_at)],
        ["शेवटचा बदल", formatDateTimeAdmin(c.updated_at)],
      ]
    : [];

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={c ? c.name : "स्पर्धक"}
      footer={
        c && (
          <>
            <Button
              variant="ghost"
              icon="trash"
              onClick={() => onDelete(c)}
              className="mr-auto danger-text"
            >
              हटवा
            </Button>
            <Button variant="secondary" onClick={onClose}>
              बंद करा
            </Button>
            <Button icon="edit" onClick={() => onEdit(c)}>
              संपादित करा
            </Button>
          </>
        )
      }
    >
      {!c ? (
        <Loading>
          <Skeleton h={200} />
        </Loading>
      ) : (
        <div className="detail">
          <p className="detail__type">
            <span className={`chip chip-${c.candidate_type}`}>
              {typeLabel(c.candidate_type)}
            </span>
          </p>
          <h3 className="detail__h">
            {c.candidate_type === "open"
              ? "नोकरी / व्यवसाय व शिक्षण"
              : c.candidate_type === "school"
                ? "शालेय तपशील"
                : "महाविद्यालयीन तपशील"}
          </h3>
          {details.status === "loading" ? (
            <Loading>
              <dl className="kv">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i}>
                    <dt>
                      <Skeleton w="60%" h={12} />
                    </dt>
                    <dd>
                      <Skeleton w="80%" h={16} />
                    </dd>
                  </div>
                ))}
              </dl>
            </Loading>
          ) : details.status === "error" || !details.data ? (
            <Alert
              tone="error"
              title="तपशील लोड होऊ शकला नाही"
              action={
                <Button size="sm" variant="secondary" onClick={details.reload}>
                  पुन्हा
                </Button>
              }
            />
          ) : (
            <dl className="kv">
              {detailRows(c.candidate_type, details.data).map(([k, v]) => (
                <div key={k}>
                  <dt>{k}</dt>
                  <dd>{v || "—"}</dd>
                </div>
              ))}
            </dl>
          )}
          <h3 className="detail__h">वैयक्तिक व संपर्क तपशील</h3>
          <dl className="kv">
            {base.map(([k, v]) => (
              <div key={k}>
                <dt>{k}</dt>
                <dd className={k.includes("आयडी") ? "mono" : ""}>{v}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </Modal>
  );
}

/* ----------------------------- delete dialog ----------------------------- */

function DeleteDialog({
  candidate: c,
  onClose,
  onDeleted,
}: {
  candidate: Candidate | null;
  onClose: () => void;
  onDeleted: (c: Candidate) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  useEffect(() => setError(undefined), [c?.id]);

  const confirm = async () => {
    if (!c) return;
    setBusy(true);
    setError(undefined);
    try {
      await deleteCandidate(c.id);
      onDeleted(c);
    } catch (e) {
      setError(candidateErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={!!c}
      onClose={() => !busy && onClose()}
      size="sm"
      title="स्पर्धक हटवायचा?"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            रद्द करा
          </Button>
          <Button
            variant="danger"
            icon="trash"
            onClick={confirm}
            loading={busy}
            loadingText="हटवत आहे…"
          >
            कायमचे हटवा
          </Button>
        </>
      }
    >
      {c && (
        <div className="stack">
          <p>
            <strong>{c.name}</strong> ({typeLabel(c.candidate_type)}, {c.phone})
            यांची नोंदणी व सर्व तपशील कायमचे हटवले जातील. ही कृती परत घेता येणार
            नाही.
          </p>
          {error && <Alert tone="error">{error}</Alert>}
        </div>
      )}
    </Modal>
  );
}
